@if (hasActiveGenerations() && generationsWithDisplayInfo().length > 0) {
  <div class="w-full p-2 bg-white/5 backdrop-blur-md rounded-lg shadow-lg border border-gray-200/10">
    <nrc-collapsible
      class="overflow-visible text-md"
      [label]="'Area Market Generation Progress'"
      [isCollapsed]="isCollapsed()"
      (collapsedEvent)="isCollapsed.set($event)"
    >
      @if (!isCollapsed()) {
        <div
          @fadeInOutAnimation
          class="space-y-3 overflow-y-auto max-h-96"
        >
          <!-- Information Note -->
          <div class="mb-3">
            <nrc-note [message]="noteMessage()"></nrc-note>
          </div>
          @for (generation of generationsWithDisplayInfo(); track generation.id) {
            <div
              class="relative flex flex-col p-4 rounded-lg bg-gray-800/60 border border-gray-700/50 transition-all duration-300 ease-in-out hover:bg-gray-700/60"
              [class]="generation.status === areaMarketGenerationStatus.COMPLETED ? 'generation-item completed' : generation.status === areaMarketGenerationStatus.ERROR ? 'generation-item error' : ''"
              [attr.data-generation-id]="generation.id"
              [attr.data-status]="generation.status"
            >
              <!-- Header Row -->
              <div class="flex items-start justify-between mb-3">
                <!-- Status Icon & Info -->
                <div class="flex items-start gap-3 flex-1 min-w-0">
                  <!-- Status Icon -->
                  <div
                    class="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0"
                    [class]="generation.displayInfo.bgClass"
                  >
                    @if (generation.displayInfo.showSpinner) {
                      <nrc-loading-spinner [class]="'flex w-full h-full items-center justify-center ' + generation.displayInfo.textClass" />
                    } @else {
                      <nrc-icon
                        [name]="generation.displayInfo.icon"
                        [fillColor]="generation.displayInfo.color"
                        class="w-5 h-5"
                      />
                    }
                  </div>

                  <!-- Job Information -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <h4 class="text-sm font-semibold text-gray-200 truncate">
                        {{ generation.marketName || 'Market Generation' }}
                      </h4>
                      <span
                        class="px-2 py-1 text-xs font-medium rounded-full flex-shrink-0"
                        [class]="generation.displayInfo.badgeClass"
                      >
                        {{ generation.displayInfo.stepLabel }}
                      </span>
                    </div>

                    <!-- Current Work Description -->
                    @if (generation.showCurrentWork && generation.currentWorkDescription) {
                      <div class="flex items-center gap-2 mb-2">
                        <nrc-icon name="gear" class="w-3 h-3 text-blue-400" fillColor="cyan" />
                        <p class="text-xs font-medium text-blue-300">{{ generation.currentWorkDescription }}</p>
                      </div>
                    }

                    <!-- Message -->
                    @if (generation.message) {
                      <p class="text-xs text-gray-400">{{ generation.message }}</p>
                    }
                  </div>
                </div>

                <!-- Action Button -->
                @if (generation.isDismissible) {
                  <button
                    class="px-3 py-1 text-xs font-medium rounded-full transition-colors duration-200 flex-shrink-0"
                    [class]="generation.status === areaMarketGenerationStatus.COMPLETED
                      ? 'text-green-500 bg-green-500/10 hover:bg-green-500/20'
                      : 'text-red-500 bg-red-500/10 hover:bg-red-500/20'"
                    (click)="clearGeneration(generation.jobId)"
                  >
                    Dismiss
                  </button>
                }
              </div>

              <!-- Progress Section -->
              @if (generation.status !== areaMarketGenerationStatus.COMPLETED && generation.status !== areaMarketGenerationStatus.ERROR) {
                <div class="space-y-2">
                  <!-- Area Progress -->
                  @if (generation.totalAreas > 0) {
                    <div class="space-y-1">
                      <div class="flex justify-between items-center text-xs">
                        <div class="flex items-center gap-1">
                          <nrc-icon name="plus" class="w-3 h-3" fillColor="cyan" />
                          <span class="text-gray-400">Areas</span>
                        </div>
                        <span class="text-blue-400 font-medium">{{ generation.areaProgressPercentage }}%</span>
                      </div>
                      <div class="w-full bg-gray-700 rounded-full h-1.5">
                        <div
                          class="bg-blue-400 h-1.5 rounded-full transition-all duration-500 ease-out"
                          [style.width.%]="generation.areaProgressPercentage"
                        ></div>
                      </div>
                      <div class="text-xs text-gray-500">
                        {{ generation.processedAreas }} / {{ generation.totalAreas }} areas
                      </div>
                    </div>
                  }

                  <!-- Element Progress -->
                  @if (generation.totalElements > 0) {
                    <div class="space-y-1">
                      <div class="flex justify-between items-center text-xs">
                        <div class="flex items-center gap-1">
                          <nrc-icon name="layer-add" class="w-3 h-3" fillColor="cyan" />
                          <span class="text-gray-400">Elements</span>
                        </div>
                        <span class="text-purple-400 font-medium">{{ generation.elementProgressPercentage }}%</span>
                      </div>
                      <div class="w-full bg-gray-700 rounded-full h-1.5">
                        <div
                          class="bg-purple-400 h-1.5 rounded-full transition-all duration-500 ease-out"
                          [style.width.%]="generation.elementProgressPercentage"
                        ></div>
                      </div>
                      <div class="text-xs text-gray-500">
                        {{ generation.processedElements }} / {{ generation.totalElements }} elements
                      </div>
                    </div>
                  }
                </div>
              }

              <!-- Completion Summary -->
              @if (generation.status === areaMarketGenerationStatus.COMPLETED) {
                <div class="mt-3 p-3 bg-green-500/10 border border-green-500/20 rounded-md">
                  <div class="flex items-start gap-2">
                    <nrc-icon name="multi-check" class="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" fillColor="success" />
                    <div class="text-xs text-green-300">
                      <strong class="font-medium">Completed:</strong> {{ generation.message }}
                    </div>
                  </div>
                </div>
              }

              <!-- Error Message -->
              @if (generation.status === areaMarketGenerationStatus.ERROR) {
                <div class="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-md">
                  <div class="flex items-start gap-2">
                    <nrc-icon name="triangle-warning" class="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fillColor="error" />
                    <div class="text-xs text-red-300">
                      <strong class="font-medium">Error:</strong> {{ generation.message }}
                    </div>
                  </div>
                </div>
              }
            </div>
          }

          @empty {
            <div class="text-center py-8 text-gray-400">
              <nrc-icon name="info-outline" class="flex w-10 h-10 mx-auto mb-3" fillColor="semi-transparent" />
              <p class="text-sm font-medium mb-1">No Active Generations</p>
              <p class="text-xs">Market area generations will appear here when started</p>
            </div>
          }
        </div>
      }
    </nrc-collapsible>
  </div>
}
